import { useEffect, useMemo, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Save, Plus, RotateCcw, DollarSign, Search } from "lucide-react";

export default function CptPricesPage() {
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [drafts, setDrafts] = useState({}); // code -> { price, description }
  const [savingCode, setSavingCode] = useState(null);
  const [newCode, setNewCode] = useState({ code: "", price: "", description: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cpt-prices");
      setPrices(data?.prices || []);
    } catch { toast.error("Could not load CPT prices"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return prices;
    return prices.filter((p) => p.code.toLowerCase().includes(q) || (p.description || "").toLowerCase().includes(q));
  }, [prices, query]);

  const setDraft = (code, field, value) => setDrafts((d) => ({ ...d, [code]: { ...(d[code] || {}), [field]: value } }));

  const save = async (row) => {
    const d = drafts[row.code] || {};
    const price = d.price !== undefined ? Number(d.price) : row.price;
    const description = d.description !== undefined ? d.description : (row.description || "");
    if (Number.isNaN(price) || price < 0) { toast.error("Invalid price"); return; }
    setSavingCode(row.code);
    try {
      await api.put(`/cpt-prices/${row.code}`, { price, description });
      toast.success(`${row.code} saved`);
      setDrafts((ds) => { const c = { ...ds }; delete c[row.code]; return c; });
      load();
    } catch { toast.error("Could not save"); }
    finally { setSavingCode(null); }
  };

  const revert = async (row) => {
    setSavingCode(row.code);
    try {
      await api.delete(`/cpt-prices/${row.code}`);
      toast.success(`${row.code} reverted to default`);
      setDrafts((ds) => { const c = { ...ds }; delete c[row.code]; return c; });
      load();
    } catch { toast.error("Could not revert"); }
    finally { setSavingCode(null); }
  };

  const addNew = async (e) => {
    e.preventDefault();
    const code = newCode.code.trim().toUpperCase();
    const price = Number(newCode.price);
    if (!code || Number.isNaN(price) || price < 0) { toast.error("Provide a CPT code and valid price"); return; }
    try {
      await api.put(`/cpt-prices/${code}`, { price, description: newCode.description.trim() });
      toast.success(`${code} added`);
      setNewCode({ code: "", price: "", description: "" });
      load();
    } catch { toast.error("Could not add CPT price"); }
  };

  return (
    <div className="px-6 sm:px-10 py-10 max-w-5xl mx-auto" data-testid="cpt-prices-page">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-md bg-[#4A6B5D] grid place-items-center text-white">
          <DollarSign className="h-6 w-6"/>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.06em] text-[#5C5850]">Configuration</div>
          <h1 className="heading text-3xl font-bold">CPT Prices</h1>
        </div>
      </div>
      <p className="mt-3 text-sm text-[#5C5850] max-w-2xl">
        Set your practice's expected revenue per CPT code. These values drive the "Est. revenue" numbers on Facility and NP Visits tabs.
        Anything you don't override uses a sensible Medicare-esque default.
      </p>

      <form onSubmit={addNew} className="mt-6 bg-white border border-[#E5E0D8] rounded-md p-4 grid sm:grid-cols-5 gap-2 items-end" data-testid="cpt-add-form">
        <div>
          <Label className="text-[11px] text-[#8A847A]">CPT / HCPCS code</Label>
          <Input required value={newCode.code} onChange={(e) => setNewCode({ ...newCode, code: e.target.value })} placeholder="99213" className="mt-1 h-10 bg-white border-[#E5E0D8] font-mono" data-testid="cpt-add-code"/>
        </div>
        <div>
          <Label className="text-[11px] text-[#8A847A]">Price ($)</Label>
          <Input required type="number" min="0" step="0.01" value={newCode.price} onChange={(e) => setNewCode({ ...newCode, price: e.target.value })} placeholder="92.00" className="mt-1 h-10 bg-white border-[#E5E0D8] font-mono" data-testid="cpt-add-price"/>
        </div>
        <div className="sm:col-span-2">
          <Label className="text-[11px] text-[#8A847A]">Description (optional)</Label>
          <Input value={newCode.description} onChange={(e) => setNewCode({ ...newCode, description: e.target.value })} placeholder="Office visit, established, 20-29 min" className="mt-1 h-10 bg-white border-[#E5E0D8]" data-testid="cpt-add-description"/>
        </div>
        <Button type="submit" className="h-10 bg-[#4A6B5D] hover:bg-[#3E594D]" data-testid="cpt-add-btn"><Plus className="h-4 w-4 mr-1"/> Add / update</Button>
      </form>

      <div className="mt-6 flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="h-4 w-4 text-[#8A847A] absolute left-2.5 top-1/2 -translate-y-1/2"/>
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search codes or description…" className="pl-8 h-10 bg-white border-[#E5E0D8]" data-testid="cpt-search"/>
        </div>
        <div className="text-xs text-[#8A847A]">{filtered.length} codes</div>
      </div>

      <div className="mt-3 bg-white border border-[#E5E0D8] rounded-md overflow-hidden">
        <table className="w-full">
          <thead><tr className="bg-[#F7F5F0]">
            <th className="text-left px-4 py-2 text-xs uppercase tracking-[0.05em] text-[#5C5850]">Code</th>
            <th className="text-left px-4 py-2 text-xs uppercase tracking-[0.05em] text-[#5C5850]">Description</th>
            <th className="text-right px-4 py-2 text-xs uppercase tracking-[0.05em] text-[#5C5850]">Price ($)</th>
            <th className="text-right px-4 py-2 text-xs uppercase tracking-[0.05em] text-[#5C5850]">Source</th>
            <th className="w-40"></th>
          </tr></thead>
          <tbody>
            {loading && <tr><td colSpan="5" className="p-8 text-center text-sm text-[#8A847A]">Loading…</td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan="5" className="p-8 text-center text-sm text-[#8A847A]">No CPT codes.</td></tr>}
            {filtered.map((row) => {
              const d = drafts[row.code] || {};
              const dirty = d.price !== undefined || d.description !== undefined;
              return (
                <tr key={row.code} className="border-t border-[#E5E0D8]" data-testid={`cpt-row-${row.code}`}>
                  <td className="px-4 py-2 font-mono font-semibold text-[#292723]">{row.code}</td>
                  <td className="px-4 py-2">
                    <Input
                      value={d.description !== undefined ? d.description : (row.description || "")}
                      onChange={(e) => setDraft(row.code, "description", e.target.value)}
                      placeholder="—"
                      className="h-8 bg-white border-[#E5E0D8] text-sm"
                      data-testid={`cpt-description-${row.code}`}
                    />
                  </td>
                  <td className="px-4 py-2 text-right">
                    <Input
                      type="number" min="0" step="0.01"
                      value={d.price !== undefined ? d.price : row.price}
                      onChange={(e) => setDraft(row.code, "price", e.target.value)}
                      className="h-8 w-24 ml-auto bg-white border-[#E5E0D8] text-sm font-mono text-right"
                      data-testid={`cpt-price-${row.code}`}
                    />
                  </td>
                  <td className="px-4 py-2 text-right">
                    {row.custom ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded border border-[#B7CDBB] bg-[#E9F0EA] text-[#3B5C46] text-[11px]">Custom</span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded border border-[#E5E0D8] bg-[#F7F5F0] text-[#8A847A] text-[11px]">Default</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <div className="flex justify-end gap-2">
                      {row.custom && (
                        <button onClick={() => revert(row)} disabled={savingCode === row.code} className="text-xs text-[#8A847A] hover:text-[#292723] inline-flex items-center gap-1" data-testid={`cpt-revert-${row.code}`}>
                          <RotateCcw className="h-3 w-3"/> Reset
                        </button>
                      )}
                      <Button
                        size="sm"
                        onClick={() => save(row)}
                        disabled={!dirty || savingCode === row.code}
                        className="h-7 px-2 bg-[#4A6B5D] hover:bg-[#3E594D] disabled:opacity-40"
                        data-testid={`cpt-save-${row.code}`}
                      >
                        <Save className="h-3.5 w-3.5 mr-1"/>Save
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
